import React, {useState, useEffect, useRef} from 'react'
import {
    Box,
    Button,
    Select,
    VStack,
    HStack,
    Text,
    IconButton,
    Portal,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {storePreviewData, getLanguageCode} from '../../utils/optimizely-preview'

const PreviewModeProjectSelector = () => {
    const [isPreviewMode, setIsPreviewMode] = useState(false)
    const [selectedProject, setSelectedProject] = useState('')
    const [availableProjects, setAvailableProjects] = useState([])
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isApplying, setIsApplying] = useState(false)
    const [error, setError] = useState(null)
    const widgetRef = useRef(null)
    const toast = useToast()
    const {locale} = useMultiSite()

    // Function to get current locale from multisite context
    const getCurrentLocale = () => {
        // Use the locale from multisite context, fallback to en-GB if not available
        return locale?.id || locale?.alias || 'en-GB'
    }

    useEffect(() => {
        const checkPreviewMode = () => {
            if (typeof window === 'undefined') return false

            // Allow forcing preview mode via console: window.__FORCE_PREVIEW__ = true
            if (window.__FORCE_PREVIEW__) return true

            // Check if we're running inside the MRT preview iframe
            try {
                // Check if we're in an iframe
                const isInIframe = window.self !== window.top
                
                if (isInIframe) {
                    // Try to access parent window to check for preview iframe ID
                    try {
                        const parentDoc = window.parent.document
                        const iframe = parentDoc.querySelector('#previewIFrame')
                        if (iframe && iframe.contentWindow === window) {
                            return true
                        }
                    } catch (e) {
                        // Cross-origin restriction, but we can still check other indicators
                        // If we're in an iframe and can't access parent, likely in preview
                        return true
                    }
                }
            } catch (e) {
                // Handle any errors gracefully
            }

            const hostname = window.location.hostname
            const url = new URL(window.location.href)
            const params = url.searchParams

            // Also show in development
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
                return true
            }

            // Check for other preview-related indicators
            return (
                params.has('preview') ||
                params.has('runtime-preview') ||
                hostname.includes('runtime-preview') ||
                hostname.includes('preview.salesforce') ||
                hostname.includes('runtime.commercecloud.com')
            )
        }

        const isPreview = checkPreviewMode()
        setIsPreviewMode(isPreview)

        if (isPreview) {
            // Check if there's already a project selected in the URL
            const url = new URL(window.location.href)
            const currentProjectId = url.searchParams.get('optimizely_project')
            if (currentProjectId) {
                setSelectedProject(currentProjectId)
            }
            
            fetchCMSProjects()
        }
    }, [])

    // Watch for URL changes to keep selected project in sync
    useEffect(() => {
        if (typeof window === 'undefined') return

        const handleUrlChange = () => {
            const url = new URL(window.location.href)
            const currentProjectId = url.searchParams.get('optimizely_project')
            if (currentProjectId && currentProjectId !== selectedProject) {
                setSelectedProject(currentProjectId)
            } else if (!currentProjectId && selectedProject) {
                // If URL doesn't have project but component has one, clear selection
                setSelectedProject('')
            }
        }

        // Listen for popstate events (back/forward navigation)
        window.addEventListener('popstate', handleUrlChange)
        
        return () => {
            window.removeEventListener('popstate', handleUrlChange)
        }
    }, [selectedProject])

    // Handle click outside to close the widget
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (widgetRef.current && !widgetRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [isOpen])

    const getCMSApiUrl = () => {
        // Use the proxy endpoint to avoid CORS issues
        return '/api/cms-proxy/projects'
    }

    const fetchCMSProjects = async () => {
        setIsLoading(true)
        setError(null)

        try {
            const cmsApiUrl = getCMSApiUrl()
            const response = await fetch(cmsApiUrl)

            if (!response.ok) {
                throw new Error(`Failed to fetch projects: ${response.statusText}`)
            }

            const projects = await response.json()
            setAvailableProjects(projects)
        } catch (err) {
            setError(err.message)
            console.error('Error fetching CMS projects:', err)
        } finally {
            setIsLoading(false)
        }
    }

    const handleProjectChange = (event) => {
        setSelectedProject(event.target.value)
    }

    const handleApply = async () => {
        if (!selectedProject || typeof window === 'undefined') return

        setIsApplying(true)
        setError(null)

        try {
            const fetchUrl = `/api/cms-proxy/projects/${selectedProject}/blocks`

            // Fetch the blocks for the selected project
            const response = await fetch(fetchUrl)

            if (!response.ok) {
                throw new Error(`Failed to fetch project blocks: ${response.status} ${response.statusText}`)
            }

            const blocksData = await response.json()

            // Check if we actually got any blocks
            if (!blocksData.blocks || blocksData.blocks.length === 0) {
                throw new Error(`No preview blocks found for project ${selectedProject}.`)
            }

            // Store the preview data in sessionStorage
            storePreviewData(blocksData, selectedProject)

            // Show success message
            const blockCount = blocksData.blocks ? blocksData.blocks.length : 0
            toast({
                title: 'Preview Applied',
                description: `Preview mode activated with ${blockCount} blocks. Page will reload with preview content.`,
                status: 'success',
                duration: 3000,
                isClosable: true
            })

            // Add the project ID to the URL and reload
            const currentUrl = new URL(window.location.href)
            currentUrl.searchParams.set('optimizely_project', selectedProject)

            // Reload the page after a short delay to show the toast
            setTimeout(() => {
                window.location.href = currentUrl.toString()
            }, 1500)
        } catch (err) {
            setError(err.message)
            toast({
                title: 'Preview Failed',
                description: err.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        } finally {
            setIsApplying(false)
        }
    }

    const toggleOpen = () => {
        setIsOpen(!isOpen)
    }

    if (!isPreviewMode) {
        return null
    }

    return (
        <Portal>
            <Box
                ref={widgetRef}
                position="fixed"
                bottom="20px"
                right="20px"
                zIndex={9999}
                bg="white"
                borderRadius="md"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.200"
                minW="280px"
            >
                <HStack
                    justify="space-between"
                    p={3}
                    borderBottom={isOpen ? '1px solid' : 'none'}
                    borderColor="gray.200"
                    cursor="pointer"
                    onClick={toggleOpen}
                >
                    <HStack>
                        <Text fontSize="sm" fontWeight="semibold" color="blue.500">
                            📝
                        </Text>
                        <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                Optimizely Preview
                            </Text>
                            {selectedProject && (
                                <Text fontSize="xs" color="green.600">
                                    Active: {availableProjects.find(p => p.id.toString() === selectedProject)?.name || selectedProject}
                                </Text>
                            )}
                        </VStack>
                    </HStack>
                    <IconButton
                        aria-label="Toggle project selector"
                        size="sm"
                        variant="ghost"
                        onClick={toggleOpen}
                    >
                        {isOpen ? '▼' : '▲'}
                    </IconButton>
                </HStack>

                {isOpen && (
                    <VStack p={4} spacing={3} align="stretch">
                        {error && (
                            <Text fontSize="xs" color="red.500">
                                Error: {error}
                            </Text>
                        )}

                        <Box>
                            <Text fontSize="xs" mb={2} color="gray.600">
                                Select CMS Project:
                            </Text>
                            <Select
                                size="sm"
                                value={selectedProject}
                                onChange={handleProjectChange}
                                bg="gray.50"
                                isDisabled={isLoading}
                                placeholder={isLoading ? 'Loading projects...' : 'Select a project'}
                            >
                                {availableProjects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name} ({project.createdBy})
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={handleApply}
                            isDisabled={!selectedProject || isLoading || isApplying}
                            isLoading={isApplying}
                            loadingText="Applying..."
                        >
                            Apply Preview
                        </Button>
                    </VStack>
                )}
            </Box>
        </Portal>
    )
}

export default PreviewModeProjectSelector
